import styled from 'styled-components';
import useGetProhibitedOperations from '../../hooks/useGetProhibitedOperations';
import greenCircleCheckmark from '../../icons/green-circle-checkmark.svg';
import warningExclamation from '../../icons/triangle-exclamation-mark.svg';

interface ProhibitedOperationsProps {
  akHash?: string;
  showProhibitedOperations?: boolean;
}

const ProhibitedOperations = ({ akHash, showProhibitedOperations }: ProhibitedOperationsProps) => {
  const { data: prohibitedOperations, isLoading } = useGetProhibitedOperations(akHash);

  if (
    isLoading ||
    !prohibitedOperations ||
    prohibitedOperations.length === 0 ||
    !showProhibitedOperations
  ) {
    return null;
  }

  return (
    <OuterContainer>
      <AppetiteBanner>
        <img src={greenCircleCheckmark} alt="Appetite Guidance Checkmark" />
        <BannerText>This industry is within appetite.</BannerText>
      </AppetiteBanner>

      <Container>
        <Heading>Ineligible Activities and Exposures</Heading>
        <DescriptionContainer>
          <img src={warningExclamation} alt="Appetite Guidance Checkmark" />
          <InfoHeading>
            The specific activities listed below would make this business ineligible for a quote.{' '}
          </InfoHeading>
        </DescriptionContainer>
        <UL>
          {prohibitedOperations.map((operation: string, index: number) => (
            <li key={index}>{operation}</li>
          ))}
        </UL>
        <InfoHeading>If your business performs any of these activities, do not proceed with this quote.</InfoHeading>
      </Container>
    </OuterContainer>
  );
};

const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 560px;
`;

const AppetiteBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #cfffeb;
  border-radius: 4px;
  padding: 7px 0 7px 8px;
  width: 100%;
  box-sizing: border-box;
`;

const BannerText = styled.p`
  color: #1d7351;
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Heading = styled.h3`
  color: ${(props) => props.theme.colors.neutral.medium};
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin: 0;

  @media (max-width: 768px) {
    margin-bottom: 8px;
  }
`;

const DescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 21px;
`;

const InfoHeading = styled.p`
  color: #626266;
  font-family: ${(props) => props.theme.font.typeface.secondary};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`;

const UL = styled.ul`
  margin: 0;
  padding-left: 16px;
  list-style: disc;
  columns: 2;

  @media (max-width: 768px) {
    columns: 1;
    margin-top: 8px;
  }

  li {
    font-family: ${(props) => props.theme.font.typeface.secondary};
    color: #626266;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 0;
    margin-left: 21px;
  }

  li::marker {
    font-size: 10px;
  }
`;

export default ProhibitedOperations;
